/**
 * Shopping — what the remaining planned days still need, minus what is already
 * in the fridge.
 *
 * A shopping list with no plan behind it is a notes app. What makes this worth
 * generating is the **subtraction**: a saved meal knows how many portions it
 * makes, so a traybake cooked on Tuesday that made four and has been eaten once
 * still owes the plan three. Asking somebody to buy those ingredients again is
 * exactly how a generated list stops being trusted, and a list that is not
 * trusted is worse than no list — you check it against the cupboard anyway, so
 * it has cost you time and bought you nothing.
 *
 * Each line keeps its provenance. "Thu dinner, Sun lunch" is what lets someone
 * decide whether a line is worth buying at all: dropping one item from the list
 * is really a decision to change two meals, and a list that hides which ones is
 * asking for a blind choice.
 *
 * Portions come down when a portion is *logged*, never when one is planned. A
 * plan is an intention and the fridge only cares what was actually eaten — the
 * whole feature falls apart the moment those two are confused.
 *
 * The fridge itself used to be a rail beside this list. It is the Prepare tab
 * now — the list only says which lines the fridge covers, and points there.
 */

import { localDate, shiftDate, useShoppingList, type ShoppingLine } from "@calorio/core";
import { Link } from "react-router-dom";

import { useCalorio } from "../app/providers";

const dayMeal = (from: { local_date: string; meal: string }) =>
  `${new Date(`${from.local_date}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
  })} ${from.meal}`;

export function Shopping() {
  const ctx = useCalorio();
  const today = localDate(ctx.timeZone);
  const to = shiftDate(today, 6);

  const list = useShoppingList(ctx, today, to);

  const lines = list.data?.lines ?? [];

  /** The list as text, for the phone in the shop that is not this one. */
  const asText = () =>
    lines
      .filter((line) => !line.covered)
      .map((line) => `${line.name} — ${Math.round(line.quantity)} ${line.unit}`)
      .join("\n");

  return (
    <div className="stack">
      <header className="plan__head">
        <div>
          <h1 className="plan__title">Shopping</h1>
          {list.data && (
            <p className="stat__label" style={{ margin: 0 }}>
              <span className="num">{lines.length}</span> lines for{" "}
              <span className="num">{list.data.planned_days}</span> remaining planned{" "}
              {list.data.planned_days === 1 ? "day" : "days"}
              {list.data.covered_lines > 0 && (
                <>
                  {" · "}
                  <span className="num">{list.data.covered_lines}</span> already covered by
                  leftovers
                </>
              )}
            </p>
          )}
        </div>

        <div className="plan__actions">
          <button
            className="plan__pill"
            onClick={() => void navigator.clipboard?.writeText(asText())}
            disabled={!lines.length}
          >
            Copy as text
          </button>
          <Link to="/plan" className="plan__pill">
            Back to plan
          </Link>
        </div>
      </header>

      {list.isLoading && <p className="muted">Working out what you need…</p>}

      {list.data && !lines.length && (
        <div className="card stack" style={{ gap: "var(--sp-2)" }}>
          <h2 className="card__title">Nothing to buy</h2>
          <p className="manual__note">
            {list.data.planned_days === 0
              ? "Nothing is planned for the days ahead, so there is nothing to shop for yet."
              : "Everything the remaining planned days need is already covered."}
          </p>
          <p className="manual__note">
            <Link to="/plan" className="muted">
              Plan the week →
            </Link>
          </p>
        </div>
      )}

      {lines.length > 0 && (
        <div className="shop">
          <div className="shop__list">
            {lines.map((line) => (
              <ShoppingRow key={line.food_item_id} line={line} />
            ))}
          </div>

          {/* The reason half the list may be struck out, said once, with the
              way to the thing doing the striking. */}
          <p className="manual__note">
            Covered lines come off what is already cooked —{" "}
            <Link to="/prepare" className="muted">
              see Prepare
            </Link>
            . Portions come down when you log one, not when you plan one.
          </p>
        </div>
      )}
    </div>
  );
}

function ShoppingRow({ line }: { line: ShoppingLine }) {
  return (
    <div className={`shop__row${line.covered ? " is-covered" : ""}`}>
      <span className="shop__name">{line.name}</span>
      <span className="num shop__qty">
        {line.covered ? "—" : `${Math.round(line.quantity)} ${line.unit}`}
      </span>
      <span className="shop__why">
        {line.covered
          ? "covered by leftovers"
          : line.requested_by.map(dayMeal).slice(0, 3).join(", ")}
      </span>
    </div>
  );
}
